import { useEffect, useRef, useState, type ReactNode } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import { isAllowedHref, type RichTextDocument } from '@oscom/shared';
import { toStoredRichText } from '../documents/editorJson';
import { richTextExtensions } from '../documents/richTextSchema';

/**
 * What the user typed into the link field -> link target. Input without a
 * scheme gets `https://`; `host:port` (a digit after the colon) and anything
 * with `://` count as written, so `example.test:8080/x` works and
 * `javascript:...` stays visible as a forbidden scheme.
 */
function linkHref(input: string): string {
  const raw = input.trim();
  const hasScheme = /^[a-z][a-z0-9+.-]*:(?!\d)/i.test(raw) || raw.includes('://');
  return hasScheme ? raw : `https://${raw}`;
}

/**
 * TipTap with bold, italic, bullet list, numbered list, link and "resolve
 * placeholder". Uncontrolled after mounting: `value` is the start content; a
 * reloaded section mounts a new editor (key with a revision).
 */
export function RichTextEditor({
  value,
  onChange,
  label,
}: {
  value: RichTextDocument;
  onChange: (doc: RichTextDocument) => void;
  label: string;
}): ReactNode {
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);
  const [linkDraft, setLinkDraft] = useState<string | undefined>(undefined);
  const [linkError, setLinkError] = useState<string | undefined>(undefined);

  const editor = useEditor({
    extensions: richTextExtensions(),
    content: value,
    shouldRerenderOnTransaction: true,
    editorProps: { attributes: { 'aria-label': label, class: 'rich-text rich-text--editing' } },
    onUpdate: ({ editor: current }) => onChangeRef.current(toStoredRichText(current.getJSON())),
  });

  if (!editor) return null;

  function applyLink(): void {
    if (!editor || linkDraft === undefined) return;
    const href = linkHref(linkDraft);
    if (!isAllowedHref(href)) {
      setLinkError('Nur Links mit https:, http: oder mailto: sind erlaubt.');
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href }).run();
    closeLinkDialog();
  }

  function openLinkDialog(): void {
    setLinkDraft((editor?.getAttributes('link').href as string | undefined) ?? '');
    setLinkError(undefined);
  }

  function closeLinkDialog(): void {
    setLinkDraft(undefined);
    setLinkError(undefined);
  }

  const toggle = (name: string, run: () => void, text: string) => (
    <button
      type="button"
      className="button button--ghost"
      aria-pressed={editor.isActive(name)}
      onClick={run}
    >
      {text}
    </button>
  );

  return (
    <div className="rich-text-editor">
      <div className="rich-text-editor__toolbar" role="toolbar" aria-label={`Format: ${label}`}>
        {toggle('bold', () => editor.chain().focus().toggleBold().run(), 'Fett')}
        {toggle('italic', () => editor.chain().focus().toggleItalic().run(), 'Kursiv')}
        {toggle('bulletList', () => editor.chain().focus().toggleBulletList().run(), 'Aufzählung')}
        {toggle(
          'orderedList',
          () => editor.chain().focus().toggleOrderedList().run(),
          'Nummerierung',
        )}
        {toggle('link', openLinkDialog, 'Link')}
        {editor.isActive('placeholder') ? (
          <button
            type="button"
            className="button button--ghost"
            onClick={() =>
              editor.chain().focus().extendMarkRange('placeholder').unsetMark('placeholder').run()
            }
          >
            Platzhalter auflösen
          </button>
        ) : null}
      </div>
      {linkDraft !== undefined ? (
        <div className="rich-text-editor__link form-field">
          <label>
            Linkadresse
            <input
              type="url"
              value={linkDraft}
              onChange={(event) => setLinkDraft(event.target.value)}
            />
          </label>
          <div className="plan-info__actions">
            <button type="button" className="button" onClick={applyLink}>
              Übernehmen
            </button>
            <button
              type="button"
              className="button"
              onClick={() => {
                editor.chain().focus().extendMarkRange('link').unsetLink().run();
                closeLinkDialog();
              }}
            >
              Link entfernen
            </button>
            <button type="button" className="button button--ghost" onClick={closeLinkDialog}>
              Abbrechen
            </button>
          </div>
          {linkError ? (
            <p className="notice notice--warning" role="alert">
              {linkError}
            </p>
          ) : null}
        </div>
      ) : null}
      <EditorContent editor={editor} />
    </div>
  );
}
